<template>
  <div class="modal fade createNewModal" id="AddElementModal" tabindex="-1" ref="addElementModalRef" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content p-15 p-md-40">
        <div class="modal-header d-block ps-0 pe-0 pt-0 pb-15 pb-md-25">
          <h5 class="modal-title fw-bold text-black">{{ title }}</h5>
        </div>
        <div class="modal-body ps-0 pe-0 pb-0 pt-15 pt-md-25">
          <Form ref="elementForm" @submit="addElement" :validation-schema="elementSchema">

            <div class="row">

              <div class="col-md-12">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="text-black fw-semibold mb-10">
                    <Field name="estPersonne" type="checkbox" v-model="estPersonne" :value="true"
                      :unchecked-value="false" class="form-check-input shadow-none" />
                    Est une Personne physique ?
                  </label>
                  <ErrorMessage name="estPersonne" class="text-danger" />
                </div>
              </div>


              <div class="col-md-12">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    {{ estPersonne ? 'Nom complet' : 'Dénomination' }}
                    <span class="text-danger">*</span>
                  </label>

                  <Field name="nom" type="text" class="form-control shadow-none fs-md-15 text-black"
                    :placeholder="estPersonne ? 'Entrer le nom complet' : 'Entrer la dénomination'" />
                  <ErrorMessage name="nom" class="text-danger" />
                </div>
              </div>

              <div v-if="estPersonne">
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Date de naissance
                      </label>
                      <Field name="dateNaissance" type="date" class="form-control shadow-none fs-md-15 text-black" />
                      <ErrorMessage name="dateNaissance" class="text-danger" />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group mb-15 mb-sm-20 mb-md-25">
                      <label class="d-block text-black fw-semibold mb-10">
                        Date de décès
                      </label>
                      <Field name="dateDeces" type="date" class="form-control shadow-none fs-md-15 text-black" />
                      <ErrorMessage name="dateDeces" class="text-danger" />
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Téléphone <span class="text-danger">*</span>
                  </label>
                  <Field name="telephone" type="text" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Entrer le téléphone de l'auteur" />
                  <ErrorMessage name="telephone" class="text-danger" />
                </div>
              </div>

              <div class="col-md-6">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Email <span class="text-danger">*</span>
                  </label>
                  <Field name="email" type="text" class="form-control shadow-none fs-md-15 text-black"
                    placeholder="Entrer l'email de l'auteur" />
                  <ErrorMessage name="email" class="text-danger" />
                </div>
              </div>



              <div class="col-md-12">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Nationalité <span class="text-danger">*</span>
                  </label>
                  <Field name="nationalite" v-model="nationaliteSelected" v-slot="{ field }">
                    <Multiselect v-model="field.value" v-bind="field" :options="nationaliteOptions" :searchable="true"
                      placeholder="Rechercher un element" noResultText="Aucun enregistrement trouvé"
                      noOptionsText="Tapez au moins deux caractères" />
                  </Field>
                  <ErrorMessage name="categorieParent" class="text-danger" />
                </div>
              </div>
            </div>
            <button
              class="default-btn transition border-0 fw-medium text-white pt-10 pb-10 ps-25 pe-25 pt-md-11 pb-md-11 ps-md-35 pe-md-35 rounded-1 fs-md-15 fs-lg-16"
              type="submit">
              {{ btntext }}
            </button>
          </Form>
        </div>
        <button type="button" class="btn-close shadow-none" data-bs-dismiss="modal" aria-label="Close"
          @click="resetValue()"></button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import ApiService from '../../services/ApiService';
import * as Yup from 'yup';
import { Auteur } from '../../models/Auteur';
import { error, success, hideModal } from '../../utils/utils';
import Multiselect from '@vueform/multiselect';

export default defineComponent({
  name: "AddAuteurModal",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect
  },
  props: {
    item: {
      type: Number,
      default: 0,
    }
  },
  setup(props, { emit }) {

    const elementSchema = Yup.object().shape({
      estPersonne: Yup.boolean().default(true),

      nom: Yup.string().required(
        () => estPersonne.value
          ? 'Le nom complet est obligatoire'
          : 'La dénomination est obligatoire'
      ),

      dateNaissance: Yup.date().nullable().notRequired(),
      dateDeces: Yup.date().nullable().notRequired(),
      telephone: Yup.string().required('Le téléphone est obligatoire'),
      nationalite: Yup.string().required('La nationalité de l\'auteur est obligatoire'),
      email: Yup.string().email('Email invalide').required('L\'email de l\'auteur est obligatoire'),

    });

    const elementForm = ref<Auteur | null>(null);
    const addElementModalRef = ref<null | HTMLElement>(null);
    const nationaliteSelected = ref<string | null>(null);
    const nationaliteOptions = ref([]);
    const estPersonne = ref(true);

    const isUPDATE = ref(false);
    const title = ref("Ajouter un auteur");
    const btntext = ref('Ajouter');

    watch(() => props.item, (newValue) => {
      getAuteur(newValue);
      isUPDATE.value = true;
      btnTitle();
    });

    watch(nationaliteSelected, (newValue) => {
      if (newValue) {
        console.log("Selected nationalite:", newValue);
        //   elementForm.value?.setFieldValue('nationalite', newValue);
      }
    });

    const btnTitle = async () => {
      if (isUPDATE.value) {
        title.value = "Modifier l'auteur";
        btntext.value = "Modifier";
      } else {
        title.value = "Ajouter un auteur";
        btntext.value = "Ajouter";
      }
    }

    const getAuteur = async (id: number) => {
      return ApiService.get("/auteur/" + id)
        .then(({ data }) => {
          const donnees = data.data;
          for (const key in donnees) {
            elementForm.value?.setFieldValue(key,
              (typeof donnees[key] === 'object' && donnees[key] !== null) ? donnees[key].id : donnees[key]
            );
          }
          emit('openmodal', addElementModalRef.value);

        })
        .catch(({ response }) => {
          error(response.data.message)
        });
    }

    const getNationalites = async () => {
      return ApiService.get("/all/pays")
        .then(({ data }) => {
          console.log("mes nationalites", data.data.data);
          nationaliteOptions.value = data.data.data.map((item: any) => ({
            value: item.id,
            label: item.nationalite
          }));
          console.log("mes nationalites options", nationaliteOptions.value);
        })
        .catch(({ response }) => {
          error(response.data.message);
        });
    };

    onMounted(() => {
      getNationalites();
      if (props.item > 0) {
        getAuteur(props.item);
      } else {
        resetValue();
      }
    });

    watch(estPersonne, (newVal) => {
    if (!newVal) {
      elementForm.value?.setFieldValue('dateNaissance', null);
      elementForm.value?.setFieldValue('dateDeces', null);
    }
  });


    const addElement = async (values: any, elementForm) => {

      values = values as Auteur;
      elementForm.resetForm();
      hideModal(addElementModalRef.value);
      emit('close', values);

      // ApiService.post("/auteurs",values)
      // .then(({ data }) => {
      //     if(data.code == 201) { 
      //     success(data.message);
      //     elementForm.resetForm();
      //     hideModal(addElementModalRef.value);
      //     emit('close');
      //     }
      // })
      // .catch(({ response }) => {
      //     error(response.data.message);
      // });
    };

    const resetValue = () => {
      const formFields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea');
      isUPDATE.value = false;
      formFields.forEach(field => {
        field.value = '';
      });
      btnTitle()
    };

    return {
      nationaliteSelected,
      elementSchema,
      addElementModalRef,
      addElement,
      elementForm,
      title, btntext,
      resetValue,
      nationaliteOptions,
      estPersonne

    };
  },
});
</script>